import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Grid,
  IconManager,
  coreIconPack,
  mediaControlsIconPack,
  Tabs,
  Layout,
  ExpandableContent,
  DescriptionList,
  CMSContent,
  useTheme,
} from '@deseretbook/react-ui'
import { useMediaQuery } from '@deseretbook/react-utils'
import qs from 'qs'
import { Logger } from '../../../../dbcom'
import { ReviewsSection } from '../../reviews'

import { JWPlayerContainer, Overview } from '..'
import { LessonOverview, Options as PlaylistOptions, Lessons } from '.'

IconManager.register(coreIconPack, mediaControlsIconPack)

const logger = new Logger({ context: 'product/video/VideoTemplate' })

/**
 * Wrapper for VideoTemplate
 *
 * @param {object} product - Spree Product + product_properties
 * @param {object} user - Current User
 * @param {string} libraryUrl - URL used for JW player
 * @param {object} playlist - Contains mediaId, and playlist data from jw player
 *
 * @returns {function} Component
 */
export const Playlist = ({ product, user, libraryUrl, playlist }) => {
  const [videoUrl, setVideoUrl] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const [playerActive, setPlayerActive] = useState(false)
  const [activeLesson, setActiveLesson] = useState(null)
  const theme = useTheme()

  const currentDate = new Date().toISOString().slice(0, 10)
  const isDeseretVideoSubscriber =
    user &&
    !!user.subscriptions.filter(
      ({ subscriptionPlan, awaitingCompletion, serviceableUntil, status }) =>
        !awaitingCompletion &&
        (serviceableUntil >= currentDate || status === 'Active') &&
        subscriptionPlan &&
        subscriptionPlan.type === 'SubscriptionPlan::DeseretVideo',
    ).length

  const lessons = playlist.data.playlist || []

  const lesson = activeLesson || (lessons && lessons[0])

  /**
   * Returns the video url for the requested label. It looks like
   * the label might relate to the resolution but I'm not positive on that.
   *
   * @param {array} sources - Sources object attached to a lesson. These contain various links to video files
   * @param {string} label - Label to match, might be resolution
   *
   * @returns {string} Video url
   */
  const getVideoUrl = (sources = [], label = '720p') => {
    const selectedSource = sources.filter((source) => source.label === label)[0] || sources[1]

    return selectedSource.file
  }

  /**
   * Sets the playerActive state to true.
   * Used mainly for styling purposes and loading the JW Player
   *
   * @param {string} url - The new URL
   */
  const activatePlayer = (url) => {
    setVideoUrl(url)
    setPlayerActive(true)
  }

  /**
   * Updates the current lesson within the url and in state
   *
   * @param {object} newLesson - lesson object from JW player
   * @see https://developer.jwplayer.com/jwplayer/reference/playlists
   */
  const handleLessonChange = (newLesson) => {
    // Add active lesson id to URL
    const state = window && window.history && window.history.state
    const url = `${location.pathname}?${qs.stringify({ lesson: newLesson.mediaid })}`

    history.pushState(state, null, url)

    // Reset the current player and set the newLesson as active
    setActiveLesson(newLesson)
    activatePlayer(getVideoUrl(newLesson.sources))
  }

  /**
   * Applies background image styles
   *
   * @param {string} direction - Direction of gradient
   *
   * @returns {object} Background image styles
   */
  const backgroundImageStyles = (direction) => ({
    backgroundImage: `linear-gradient(${direction}, ${theme.palette.dark.main} 0%, transparent 50%), url(${lesson.image})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  })

  /**
   * Sets the active tab for smaller views
   *
   * @param {number} tabIndex - Which tab to set as active
   */
  const updateActiveTab = (tabIndex) => {
    setActiveTab(tabIndex)
  }

  // Applies backdrop to cover full width of screen
  const isFullDesktop = useMediaQuery({ minWidth: 1120 })
  const isDesktopOrLarger = useMediaQuery({ minWidth: 768 })

  // For when the content reaches its tablet breakpoint
  const isTabletOrSmaller = useMediaQuery({ maxWidth: 767 })
  // For when the content reaches its mobile breakpoint
  const isMobileOrSmaller = useMediaQuery({ maxWidth: 639 })

  /**
   * Lessons component used for easy reuse
   *
   * @returns {function} React Component
   */
  const LessonsComponent = () => (
    <Lessons
      lessons={lessons}
      activeLesson={activeLesson}
      handleLessonChange={handleLessonChange}
      isDisabled={!isDeseretVideoSubscriber}
    />
  )

  /**
   * Options component used for easy reuse
   *
   * @returns {function} React Component
   */
  const OptionsComponent = () => (
    <PlaylistOptions
      user={user}
      videoUrl={getVideoUrl(lesson.sources)}
      activatePlayer={activatePlayer}
      isDeseretVideoSubscriber={isDeseretVideoSubscriber}
    />
  )

  /**
   * Overview component used for easy reuse
   *
   * Displays Overview component when no lesson is selected
   *
   * Otherwise displays the lesson overview
   *
   * @returns {function} React Component
   */
  const OverviewComponent = () => {
    const isDark = !playerActive && isFullDesktop

    return activeLesson ? (
      <LessonOverview lesson={activeLesson} isDark={isDark} />
    ) : (
      <Overview product={product} isDark={isDark} updateActiveTab={updateActiveTab} />
    )
  }

  /**
   * Sets the current lesson if there is a matching ID found within the URL
   */
  const initialize = () => {
    const { lesson: lessonId = '' } = qs.parse(location.search.slice(1))

    const newLesson = lessons.filter((lessonList) => lessonList.mediaid === lessonId)[0]

    if (newLesson) {
      setActiveLesson(newLesson)
    }
  }

  useEffect(() => {
    initialize()
  }, [])

  return (
    <Layout>
      <Layout.Content>
        {/* Video Player and Overview Section */}
        {/* Larger than tablet layout */}
        {isDesktopOrLarger && (
          <Layout.Section
            style={{
              position: 'relative',
              // Darkens the background with the themed color when in desktop view
              ...(isDesktopOrLarger &&
                !playerActive && {
                  color: 'white',
                  backgroundColor: theme.palette.dark.main,
                }),
            }}
            isPaddinglessHorizontal={playerActive}
          >
            <Grid
              columns={playerActive || !isFullDesktop ? Grid.fr(1) : [30, Grid.fr(1)]}
              rows="auto"
              gap={1}
              alignItems="end"
              style={{ zIndex: 10, position: 'relative' }}
            >
              {/* Video Information and rating */}
              {!playerActive && isFullDesktop && (
                <Grid.Item>
                  <OverviewComponent />
                  <Box paddingTop={2} isInline>
                    <OptionsComponent />
                  </Box>
                </Grid.Item>
              )}

              {/* Video Player */}
              <Grid.Item>
                {playerActive ? (
                  <Box
                    maxWidth={70}
                    isFullwidth
                    isFullheight
                    maxHeight={40}
                    padding={1}
                    style={{ margin: '0 auto', backgroundColor: theme.palette.dark.main }}
                  >
                    <JWPlayerContainer
                      libraryUrl={libraryUrl}
                      videoUrl={videoUrl}
                      videoPoster={lesson.image}
                      videoContainerId="jw-player-container"
                    />
                  </Box>
                ) : (
                  <Box
                    isFullwidth
                    height="55vh"
                    maxHeight={40}
                    style={backgroundImageStyles(isFullDesktop ? 'to right' : 'to top')}
                  />
                )}
              </Grid.Item>
            </Grid>
            {isFullDesktop && (
              <div
                // Applies a nearly full-width backdrop for video section
                style={{
                  backgroundColor: theme.palette.dark.main,
                  position: 'absolute',
                  top: '-1.5rem',
                  left: '-60%',
                  width: '200vw',
                  height: playerActive ? '100%' : '65vh',
                  maxHeight: '44.5rem',
                  zIndex: 5,
                }}
              />
            )}
          </Layout.Section>
        )}
        {isDesktopOrLarger && (playerActive || !isFullDesktop) && (
          <Layout.Section>
            <Box isColumn alignItems="start">
              <OverviewComponent />
              <Box paddingTop={2} isInline>
                <OptionsComponent />
              </Box>
            </Box>
          </Layout.Section>
        )}

        {/* Tablet and smaller layout */}
        {isTabletOrSmaller && (
          <Layout.Section>
            {playerActive ? (
              <JWPlayerContainer
                libraryUrl={libraryUrl}
                videoUrl={videoUrl}
                videoPoster={lesson.image}
                videoContainerId="jw-player-container"
              />
            ) : (
              <Box height={15} style={backgroundImageStyles('to top')}>
                <Box isFullwidth isFullheight alignItems="center" justifyContent="center" />
              </Box>
            )}
            <OverviewComponent />
            <Box paddingTop={1} isColumn={isTabletOrSmaller}>
              <OptionsComponent />
            </Box>
          </Layout.Section>
        )}
        {/* Details and Reviews Sections */}
        {/* Larger than tablet Section */}
        {isDesktopOrLarger && (
          <Layout.Section>
            <Grid gap={1} columns={[Grid.minMax(24, Grid.fr(1)), Grid.minMax(18, 26)]}>
              <Grid.Item>
                <Tabs activeTab={activeTab}>
                  <Tabs.TabList tabType="default" isFullwidth={isMobileOrSmaller}>
                    <Tabs.Tab>Details</Tabs.Tab>
                    <Tabs.Tab>Reviews</Tabs.Tab>
                    <Tabs.Tab>Trailers</Tabs.Tab>
                  </Tabs.TabList>
                  {/* Details Panel */}
                  <Tabs.Panel>
                    <Box paddingVertical={1}>
                      <ExpandableContent collapsedHeight={10}>
                        <CMSContent
                          itsNotDangerousBecause="It is dangerous. Fix this ASAP!!!!!"
                          dangerouslySetInnerHTML={{
                            __html: product.description,
                          }}
                        />
                      </ExpandableContent>
                    </Box>
                    <DescriptionList>
                      {product.productProperties.map((productProperty) => (
                        <React.Fragment key={productProperty.id}>
                          <DescriptionList.Term>
                            {productProperty.property.presentation}
                          </DescriptionList.Term>
                          <DescriptionList.Description>
                            {productProperty.value}
                          </DescriptionList.Description>
                        </React.Fragment>
                      ))}
                    </DescriptionList>
                  </Tabs.Panel>
                  {/* Reviews Panel */}
                  <Tabs.Panel>
                    <ReviewsSection product={product} />
                  </Tabs.Panel>
                  {/* Trailers Panel */}
                  <Tabs.Panel>Trailer</Tabs.Panel>
                </Tabs>
              </Grid.Item>
              <Grid.Item>
                <LessonsComponent />
              </Grid.Item>
            </Grid>
          </Layout.Section>
        )}
        {/* Mobile Tabs Section */}
        {isTabletOrSmaller && (
          <Layout.Section>
            <Tabs activeTab={activeTab}>
              <Tabs.TabList tabType="default" isFullwidth={isTabletOrSmaller}>
                <Tabs.Tab>Lessons</Tabs.Tab>
                <Tabs.Tab>Details</Tabs.Tab>
                <Tabs.Tab>Reviews</Tabs.Tab>
                <Tabs.Tab>Trailers</Tabs.Tab>
              </Tabs.TabList>
              {/* Lessons Panel */}
              <Tabs.Panel>
                <LessonsComponent />
              </Tabs.Panel>
              {/* Details Panel */}
              <Tabs.Panel>
                <Box paddingVertical={1}>
                  <ExpandableContent collapsedHeight={10}>
                    <CMSContent
                      itsNotDangerousBecause="It is dangerous. Fix this ASAP!!!!!"
                      dangerouslySetInnerHTML={{
                        __html: product.description,
                      }}
                    />
                  </ExpandableContent>
                </Box>
                <DescriptionList>
                  {product.productProperties.map((productProperty) => (
                    <React.Fragment key={productProperty.id}>
                      <DescriptionList.Term>
                        {productProperty.property.presentation}
                      </DescriptionList.Term>
                      <DescriptionList.Description>
                        {productProperty.value}
                      </DescriptionList.Description>
                    </React.Fragment>
                  ))}
                </DescriptionList>
              </Tabs.Panel>
              {/* Reviews Panel */}
              <Tabs.Panel>
                <ReviewsSection product={product} />
              </Tabs.Panel>
              {/* Trailers Panel */}
              <Tabs.Panel>Trailer</Tabs.Panel>
            </Tabs>
          </Layout.Section>
        )}
      </Layout.Content>
    </Layout>
  )
}

Playlist.propTypes = {
  user: PropTypes.shape({
    subscriptions: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  product: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    id: PropTypes.number,
    reviewsCount: PropTypes.number,
    avgRating: PropTypes.string,
    productProperties: PropTypes.arrayOf(PropTypes.any),
  }),
  libraryUrl: PropTypes.string,
  video: PropTypes.PropTypes.shape({
    url: PropTypes.string,
    poster: PropTypes.string,
  }),
  trailer: PropTypes.shape({
    url: PropTypes.string,
    poster: PropTypes.string,
  }),
  playlist: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.any),
    mediaId: PropTypes.string,
  }),
}
